import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { Goal, GoalCategory, GoalStatus } from '../../types/Goal';
import { goalService } from '../../services/goalService';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import PageHeader from '../common/PageHeader';
import './LifeOKR.css';

interface LifeOKRProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type LifeAspect = 'family' | 'health' | 'mind' | 'finance';

interface AspectData {
  category: GoalCategory;
  label: string;
  goals: Goal[];
  averageProgress: number;
}

const LifeOKR: React.FC<LifeOKRProps> = ({ user, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedAspect, setSelectedAspect] = useState<LifeAspect | null>(null);
  const [availableGoals, setAvailableGoals] = useState<Goal[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [activeTab, setActiveTab] = useState<'chart' | 'goals'>('chart');

  // Map life aspects to goal categories
  const aspectCategoryMap: Record<LifeAspect, GoalCategory> = {
    family: GoalCategory.FAMILY,
    health: GoalCategory.HEALTH,
    mind: GoalCategory.MIND,
    finance: GoalCategory.FINANCE
  };

  // Helper function to safely get progress as a number
  const getProgressNumber = (progress: any): number => {
    if (typeof progress === 'number' && !isNaN(progress)) {
      return progress;
    }
    const num = Number(progress);
    return isNaN(num) ? 0 : num;
  };

  // Load goals
  useEffect(() => {
    if (user) {
      loadGoals();
    }
  }, [user]);

  // Handle clicks outside menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const loadGoals = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await goalService.getUserGoals({
        user_id: user.id.toString()
      });
      
      if (result.success && result.goals) {
        const convertedGoals = result.goals.map(goal => goalService.convertFirebaseGoal(goal));
        setGoals(convertedGoals);
      } else {
        setGoals([]);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load goals');
      console.error('Error loading goals:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get goals for each aspect
  const getAspectData = (): AspectData[] => {
    const aspects: LifeAspect[] = ['family', 'health', 'mind', 'finance'];
    
    return aspects.map(aspect => {
      const category = aspectCategoryMap[aspect];
      const aspectGoals = goals.filter(g => g.category === category);
      
      // Calculate average progress, ensuring we handle undefined/null values
      let averageProgress = 0;
      if (aspectGoals.length > 0) {
        const validGoals = aspectGoals.filter(g => {
          const progress = getProgressNumber(g.progress);
          return !isNaN(progress);
        });
        if (validGoals.length > 0) {
          const sum = validGoals.reduce((sum, g) => sum + getProgressNumber(g.progress), 0);
          averageProgress = Math.round(sum / validGoals.length);
        }
      }
      
      return {
        category,
        label: aspect.charAt(0).toUpperCase() + aspect.slice(1),
        goals: aspectGoals,
        averageProgress
      };
    });
  };

  // Get data for radar chart
  const getRadarData = () => {
    const aspectData = getAspectData();
    return aspectData.map(aspect => ({
      aspect: aspect.label,
      progress: Math.max(0, Math.min(100, aspect.averageProgress || 0)),
      fullMark: 100
    }));
  };

  // Handle linking a goal to an aspect
  const handleLinkGoal = async (goalId: string, aspect: LifeAspect) => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      
      const category = aspectCategoryMap[aspect];
      // Ensure category is sent as a string value (enum values are strings, but be explicit)
      const categoryValue = category.toString();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Linking goal:', goalId, 'to category:', categoryValue, 'for aspect:', aspect);
      }
      
      const result = await goalService.updateGoal({
        id: goalId,
        category: categoryValue
      });
      
      if (result.success) {
        await loadGoals();
        setShowLinkModal(false);
        setSelectedAspect(null);
      } else {
        throw new Error('Failed to link goal');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to link goal');
      console.error('Error linking goal:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle unlinking a goal from an aspect
  const handleUnlinkGoal = async (goalId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to goal detail
    
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      
      // Set category to OTHER to unlink from life aspect
      const result = await goalService.updateGoal({
        id: goalId,
        category: GoalCategory.OTHER.toString()
      });
      
      if (result.success) {
        await loadGoals();
      } else {
        throw new Error('Failed to unlink goal');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to unlink goal');
      console.error('Error unlinking goal:', err);
    } finally {
      setLoading(false);
    }
  };

  // Open link modal for a specific aspect
  const openLinkModal = (aspect: LifeAspect) => {
    setSelectedAspect(aspect);
    // Get goals that are not already in the target aspect
    const targetCategory = aspectCategoryMap[aspect];
    const available = goals.filter(g => g.category !== targetCategory);
    setAvailableGoals(available);
    setShowLinkModal(true);
  };

  // Handle goal click to navigate to detail
  const handleGoalClick = (goalId: string) => {
    navigate(`/goals/${goalId}`);
  };

  const aspectData = getAspectData();
  const radarData = getRadarData();

  if (!user) {
    return (
      <div className="life-okr">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Life OKR</h1>}
        />
        <div className="auth-required">
          <p>Please sign in to view your Life OKR.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="life-okr">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">Life OKR</h1>}
        rightActions={
          <div className="life-okr-menu-container" ref={menuRef}>
            <button
              className="life-okr-menu-btn"
              onClick={() => setShowMenu(!showMenu)}
              title="Menu"
            >
              ⋮
            </button>
            {showMenu && (
              <div className="life-okr-menu-dropdown">
                <button
                  className="life-okr-menu-item"
                  onClick={() => {
                    navigate('/goals');
                    setShowMenu(false);
                  }}
                >
                  Go to Goals
                </button>
              </div>
            )}
          </div>
        }
      />

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="life-okr-content">
        {loading ? (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {/* Radar Chart Section */}
            {activeTab === 'chart' && (
              <div className="life-okr-radar-section">
                <div className="life-okr-section-header">
                  <span className="life-okr-section-title">/balance</span>
                </div>
                <div className="life-okr-radar-container">
                  <ResponsiveContainer width="100%" height={400}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#e0e0e0" />
                      <PolarAngleAxis 
                        dataKey="aspect" 
                        tick={{ fill: '#666666', fontSize: 12, fontFamily: 'SF Mono, Monaco, Inconsolata, Roboto Mono, Source Code Pro, monospace' }}
                      />
                      <PolarRadiusAxis 
                        angle={90} 
                        domain={[0, 100]}
                        tick={{ fill: '#666666', fontSize: 10 }}
                      />
                      <Radar
                        name="Progress"
                        dataKey="progress"
                        stroke="#333333"
                        fill="#333333"
                        fillOpacity={0.3}
                        dot={{ fill: '#333333', r: 4 }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '0.25rem',
                          color: '#333333'
                        }}
                        formatter={(value: number) => {
                          const numValue = typeof value === 'number' && !isNaN(value) ? value : 0;
                          return [`${numValue}%`, 'Progress'];
                        }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Aspects Grid */}
            {activeTab === 'goals' && (
              <div className="life-okr-aspects-grid">
                {aspectData.map((aspect) => (
                  <div key={aspect.category} className="life-okr-aspect-card">
                    <div className="life-okr-aspect-header">
                      <span className="life-okr-aspect-title">/{aspect.label.toLowerCase()}</span>
                      <button
                        className="life-okr-link-btn"
                        onClick={() => openLinkModal(aspect.label.toLowerCase() as LifeAspect)}
                        title="Link a goal to this aspect"
                      >
                        +
                      </button>
                    </div>
                    
                    <div className="life-okr-aspect-progress">
                      <div className="life-okr-progress-bar">
                        <div
                          className="life-okr-progress-fill"
                          style={{ width: `${Math.max(0, Math.min(100, aspect.averageProgress))}%` }}
                        />
                      </div>
                      <span className="life-okr-progress-text">{Math.max(0, Math.min(100, aspect.averageProgress))}%</span>
                    </div>

                    <div className="life-okr-goals-list">
                      {aspect.goals.length === 0 ? (
                        <div className="life-okr-empty-aspect">
                          <p>No goals linked</p>
                          <button
                            className="life-okr-link-btn-small"
                            onClick={() => openLinkModal(aspect.label.toLowerCase() as LifeAspect)}
                          >
                            Link Goal
                          </button>
                        </div>
                      ) : (
                        aspect.goals.map((goal) => {
                          const goalProgress = Math.max(0, Math.min(100, getProgressNumber(goal.progress)));
                          return (
                            <div
                              key={goal.id}
                              className="life-okr-goal-item"
                              onClick={() => handleGoalClick(goal.id)}
                            >
                              <div className="life-okr-goal-item-header">
                                <span className="life-okr-goal-title">{goal.title}</span>
                                <button
                                  className="life-okr-unlink-btn"
                                  onClick={(e) => handleUnlinkGoal(goal.id, e)}
                                  title="Unlink goal from this aspect"
                                >
                                  ×
                                </button>
                              </div>
                              <div className="life-okr-goal-separator"></div>
                              <div className="life-okr-goal-progress-mini">
                                <div className="life-okr-goal-progress-bar-mini">
                                  <div
                                    className="life-okr-goal-progress-fill-mini"
                                    style={{ width: `${goalProgress}%` }}
                                  />
                                </div>
                                <span className="life-okr-goal-progress-text">{goalProgress.toFixed(0)}%</span>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Tabbar */}
      <div className="life-okr-tabbar">
        <button
          className={`life-okr-tab ${activeTab === 'chart' ? 'active' : ''}`}
          onClick={() => setActiveTab('chart')}
        >
          <span className="life-okr-tab-label">Chart</span>
        </button>
        <button
          className={`life-okr-tab ${activeTab === 'goals' ? 'active' : ''}`}
          onClick={() => setActiveTab('goals')}
        >
          <span className="life-okr-tab-label">Goals</span>
        </button>
      </div>

      {/* Link Goal Modal */}
      {showLinkModal && selectedAspect && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowLinkModal(false);
            setSelectedAspect(null);
          }
        }}>
          <div className="modal-content life-okr-link-modal">
            <div className="modal-header">
              <h2>Link Goal to {selectedAspect.charAt(0).toUpperCase() + selectedAspect.slice(1)}</h2>
              <button className="close-btn" onClick={() => {
                setShowLinkModal(false);
                setSelectedAspect(null);
              }}>×</button>
            </div>
            
            <div className="life-okr-link-modal-content">
              {availableGoals.length === 0 ? (
                <div className="life-okr-no-goals">
                  <p>No goals available to link.</p>
                  <button
                    className="life-okr-create-goal-btn"
                    onClick={() => {
                      setShowLinkModal(false);
                      navigate('/goals');
                    }}
                  >
                    Create New Goal
                  </button>
                </div>
              ) : (
                <div className="life-okr-goals-select-list">
                  {availableGoals.map((goal) => (
                    <div
                      key={goal.id}
                      className="life-okr-goal-select-item"
                      onClick={() => handleLinkGoal(goal.id, selectedAspect)}
                    >
                      <div className="life-okr-goal-select-info">
                        <span className="life-okr-goal-select-title">{goal.title}</span>
                        <span className="life-okr-goal-select-category">{goal.category}</span>
                      </div>
                      <span className="life-okr-goal-select-progress">{getProgressNumber(goal.progress).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LifeOKR;

